import React from 'react';

const LoadingSpinner = () => (
  <div style={styles.container}>
    <div style={styles.inner}>
      {/* ECG Line */}
      <svg viewBox="0 0 600 120" style={styles.svg} preserveAspectRatio="xMidYMid meet">
        <defs>
          <linearGradient id="ecgGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="var(--primary-color, #0d6efd)" stopOpacity="0" />
            <stop offset="40%" stopColor="var(--primary-color, #0d6efd)" stopOpacity="1" />
            <stop offset="100%" stopColor="var(--primary-color, #0d6efd)" stopOpacity="1" />
          </linearGradient>
          <filter id="ecgGlow">
            <feGaussianBlur stdDeviation="2" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          <clipPath id="ecgReveal">
            <rect x="-600" y="0" width="600" height="120">
              <animate attributeName="x" from="-600" to="600" dur="2.5s" repeatCount="indefinite" />
            </rect>
          </clipPath>
        </defs>

        {/* Background grid lines */}
        {[20, 40, 60, 80, 100].map((y) => (
          <line
            key={y}
            x1="0"
            y1={y}
            x2="600"
            y2={y}
            stroke="var(--primary-color, #0d6efd)"
            strokeOpacity="0.06"
            strokeWidth="0.5"
          />
        ))}
        {Array.from({ length: 25 }, (_, i) => i * 24).map((x) => (
          <line
            key={x}
            x1={x}
            y1="0"
            x2={x}
            y2="120"
            stroke="var(--primary-color, #0d6efd)"
            strokeOpacity="0.06"
            strokeWidth="0.5"
          />
        ))}

        {/* ECG waveform path — realistic PQRST */}
        <g clipPath="url(#ecgReveal)" filter="url(#ecgGlow)">
          <path
            d={ecgPath()}
            fill="none"
            stroke="url(#ecgGradient)"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </g>

        {/* Scanning dot */}
        <circle r="4" fill="var(--primary-color, #0d6efd)" opacity="0.9">
          <animate attributeName="cx" from="0" to="600" dur="2.5s" repeatCount="indefinite" />
          <animate attributeName="cy" values={dotPath()} dur="2.5s" repeatCount="indefinite" />
        </circle>
        <circle r="8" fill="var(--primary-color, #0d6efd)" opacity="0.2">
          <animate attributeName="cx" from="0" to="600" dur="2.5s" repeatCount="indefinite" />
          <animate attributeName="cy" values={dotPath()} dur="2.5s" repeatCount="indefinite" />
        </circle>
      </svg>

      {/* Text */}
      <p style={styles.text}>
        Loading
        <span style={styles.dots}>
          <Dots />
        </span>
      </p>
    </div>
  </div>
);

/* Realistic ECG PQRST waveform — repeated twice across 600px */
function ecgPath() {
  // Single heartbeat segment (~300px wide)
  const beat = (ox) => {
    const o = ox;
    return [
      // Baseline
      `L${o} 60`,
      // P wave (small bump)
      `Q${o + 30} 48 ${o + 50} 60`,
      // Flat PQ segment
      `L${o + 65} 60`,
      // Q dip
      `L${o + 75} 68`,
      // R spike (sharp up)
      `L${o + 90} 15`,
      // S dip (sharp down)
      `L${o + 105} 80`,
      // Return to baseline
      `L${o + 115} 60`,
      // Flat ST segment
      `L${o + 140} 60`,
      // T wave (rounded bump)
      `Q${o + 165} 42 ${o + 190} 60`,
      // Baseline to end of beat
      `L${o + 300} 60`,
    ].join(' ');
  };
  return `M0 60 ${beat(0)} ${beat(300)}`;
}

/* Y values for the scanning dot following the ECG path */
function dotPath() {
  // Approximate Y positions at evenly spaced time intervals
  return '60;60;48;60;60;68;15;80;60;60;42;60;60;60;48;60;60;68;15;80;60;60;42;60;60';
}

/* Animated dots component */
const Dots = () => (
  <>
    {[0, 1, 2].map((i) => (
      <span key={i} style={{ ...styles.dot, animationDelay: `${i * 0.3}s` }}>
        .
      </span>
    ))}
  </>
);

const styles = {
  container: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    background: 'var(--bs-body-bg, #fff)',
  },
  inner: {
    textAlign: 'center',
    width: '100%',
    maxWidth: '340px',
    padding: '0 1rem',
  },
  svg: {
    width: '100%',
    height: 'auto',
    display: 'block',
    marginBottom: '1rem',
  },
  text: {
    color: 'var(--primary-color, #0d6efd)',
    fontSize: '0.95rem',
    fontWeight: 500,
    letterSpacing: '0.05em',
    margin: 0,
    opacity: 0.8,
  },
  dots: {
    display: 'inline-block',
    width: '1.5em',
    textAlign: 'left',
  },
  dot: {
    animation: 'ecgDotPulse 0.9s infinite',
    opacity: 0,
  },
};

// Inject keyframes once
if (typeof document !== 'undefined' && !document.getElementById('ecg-loader-styles')) {
  const style = document.createElement('style');
  style.id = 'ecg-loader-styles';
  style.textContent = `
    @keyframes ecgDotPulse {
      0%, 20% { opacity: 0; }
      50% { opacity: 1; }
      100% { opacity: 0; }
    }
  `;
  document.head.appendChild(style);
}

export default LoadingSpinner;
